import { useEffect, useRef, useState } from "react";

import { ChatStreamError, streamChat } from "../api/chat";
import { createRemoteSession, saveQuery, saveReply } from "../api/history";
import { isLocalSessionId, useChatSessions, titleFromMessage } from "../hooks/useChatSessions";
import { ChatInput } from "./ChatInput";
import { DataCard, DownloadReportButton } from "./DataCard";
import { EmptyState } from "./EmptyState";
import { MessageBubble } from "./MessageBubble";
import { Sidebar } from "./Sidebar";
import type { ChatData, ChatMessage, CityReportData, DisplayMessage } from "../types";

const MAX_HISTORY_MESSAGES = 10;

// The backend doesn't guarantee small, evenly-paced token chunks — some
// replies (e.g. the coordinates shortcut in backend/routers/chat.py) arrive
// as a single "token" event with the whole reply text. To always get a
// word-by-word typing effect in the UI, we reveal `fullText` gradually on a
// timer instead of rendering whatever arrived immediately.
const REVEAL_INTERVAL_MS = 30;

// How close to the bottom (px) still counts as "at the bottom" for auto-scroll.
const STICK_THRESHOLD_PX = 80;

interface StreamingState {
  sessionId: string;
  status: string | null;
  fullText: string;
  displayedText: string;
  dataList: CityReportData[];
  pendingFinal: { content: string; data: ChatData } | null;
}

export function ChatPage() {
  const {
    sessions,
    activeSessionId,
    activeSession,
    radiusKm,
    setRadiusKm,
    setActiveSessionId,
    createSession,
    deleteSession,
    updateSessionMessages,
    setSessionTitle,
    replaceSessionId,
  } = useChatSessions();

  const [streaming, setStreaming] = useState<StreamingState | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);
  // Follow the streaming text only while the user is at (or near) the bottom.
  // Scrolling up to read earlier content turns this off, so the view isn't
  // yanked back down on every revealed word; scrolling back down (or sending a
  // new message) turns it on again.
  const stickToBottomRef = useRef(true);

  function handleScroll(e: React.UIEvent<HTMLDivElement>) {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD_PX;
  }

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    // While actively streaming, the anchor's position keeps shifting (new
    // words, tables expanding) faster than a "smooth" scroll can finish, so
    // each call cancels the previous animation and the view never catches
    // up. Snap instantly during streaming; use a smooth scroll otherwise.
    scrollAnchorRef.current?.scrollIntoView({ behavior: streaming ? "auto" : "smooth", block: "end" });
  }, [activeSession?.messages.length, streaming]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  // Drives the word-by-word reveal: on every tick, pull one more word from
  // `fullText` into `displayedText`. Once the finished reply ("pendingFinal")
  // has fully caught up, commit it to the session and end streaming.
  useEffect(() => {
    if (!streaming) return;
    if (streaming.displayedText.length >= streaming.fullText.length) {
      if (streaming.pendingFinal) {
        const { content, data } = streaming.pendingFinal;
        updateSessionMessages(streaming.sessionId, (messages) => [...messages, { role: "assistant", content, data }]);
        setStreaming(null);
      }
      return;
    }

    const timer = setTimeout(() => {
      setStreaming((prev) => {
        if (!prev) return prev;

        const remaining = prev.fullText.slice(prev.displayedText.length);
        const match = remaining.match(/^\s*\S+/); // next word, including any leading whitespace
        const chunk = match ? match[0] : remaining;

        return { ...prev, displayedText: prev.displayedText + chunk };
      });
    }, REVEAL_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [streaming, updateSessionMessages]);

  async function handleSend(text: string) {
    let sessionId = activeSessionId;

    if (!sessionId) {
      sessionId = createSession();
    }

    const session = sessions.find((s) => s.id === sessionId);
    const isFirstMessage = !session || session.messages.length === 0;

    // Persist to the database. A brand-new chat only exists locally until now;
    // create it server-side and swap in the real _id. Failures never block chatting.
    if (isLocalSessionId(sessionId)) {
      try {
        const remoteId = await createRemoteSession(titleFromMessage(text));
        replaceSessionId(sessionId, remoteId);
        sessionId = remoteId;
      } catch {
        // stay local-only for this chat
      }
    }

    const persistedSessionId = isLocalSessionId(sessionId) ? null : sessionId;
    const queryIdPromise: Promise<string | null> = persistedSessionId
      ? saveQuery(persistedSessionId, text).catch(() => null)
      : Promise.resolve(null);

    if (isFirstMessage) {
      setSessionTitle(sessionId, titleFromMessage(text));
    }

    stickToBottomRef.current = true; // a new question: follow the answer as it arrives
    const userMessage: DisplayMessage = { role: "user", content: text };
    updateSessionMessages(sessionId, (messages) => [...messages, userMessage]);

    const priorMessages = session ? session.messages : [];
    const messagesForApi: ChatMessage[] = [...priorMessages, userMessage]
      .slice(-MAX_HISTORY_MESSAGES)
      .map((m) => ({ role: m.role, content: m.content }));

    setStreaming({ sessionId, status: "Working...", fullText: "", displayedText: "", dataList: [], pendingFinal: null });

    const collectedData: CityReportData[] = [];
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat({
        messages: messagesForApi,
        radiusKm,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.type === "data" && event.data) {
            collectedData.push(...(Array.isArray(event.data) ? event.data : [event.data]).filter((d) => d != null));
          } else if (event.type === "done" && persistedSessionId) {
            const content = event.reply || "";
            const data = event.data ?? (collectedData.length ? collectedData : null);

            if (content) {
              queryIdPromise.then((queryId) => {
                if (queryId) saveReply(persistedSessionId, queryId, content, data).catch(() => {});
              });
            }
          }

          setStreaming((prev) => {
            const current: StreamingState =
              prev ?? { sessionId: sessionId!, status: null, fullText: "", displayedText: "", dataList: [], pendingFinal: null };

            switch (event.type) {
              case "status":
                return { ...current, status: event.message };

              case "data": {
                if (!event.data) return current;
                const incoming = (Array.isArray(event.data) ? event.data : [event.data]).filter(
                  (d): d is CityReportData => d != null
                );
                return { ...current, dataList: [...current.dataList, ...incoming] };
              }

              case "token":
                return { ...current, status: null, fullText: current.fullText + event.content };

              case "done": {
                const finalData = event.data ?? (current.dataList.length ? current.dataList : null);
                const content = event.reply || current.fullText;

                // Grow fullText to the final reply (some paths, e.g. the
                // coordinates shortcut, send the whole reply in one "token"
                // event already, others may reach "done" with none at all)
                // so the reveal effect has something to type out, then hand
                // off the finished message for it to commit once caught up.
                return {
                  ...current,
                  status: null,
                  fullText: content,
                  pendingFinal: { content, data: finalData },
                };
              }

              case "error": {
                updateSessionMessages(sessionId!, (messages) => [
                  ...messages,
                  { role: "assistant", content: event.message, data: null },
                ]);

                return null;
              }

              default:
                return current;
            }
          });
        },
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;

      const message =
        err instanceof ChatStreamError
          ? err.message
          : "The server is temporarily unavailable.";

      updateSessionMessages(sessionId, (messages) => [...messages, { role: "assistant", content: message, data: null }]);
      setStreaming(null);
    } finally {
      abortRef.current = null;
    }
  }

  return (
    <div className="app-layout">
      <Sidebar
        sessions={sessions}
        activeSessionId={activeSessionId}
        radiusKm={radiusKm}
        onSelectSession={setActiveSessionId}
        onDeleteSession={deleteSession}
        onNewChat={createSession}
        onRadiusChange={setRadiusKm}
      />

      <main className="chat-main">
        <h1 className="page-title">EV Insights Assistant</h1>
        <p className="page-caption">Ask about EV registrations, demand near a city, or a specific lat/lon</p>

        <div className="chat-scroll" onScroll={handleScroll}>
          {!streaming && (!activeSession || activeSession.messages.length === 0) ? (
            <EmptyState />
          ) : (
            <>
              {activeSession?.messages.map((msg, i) => <MessageBubble key={i} message={msg} />)}

              {streaming && (
                <div className="chat-message assistant">
                  <div className="chat-avatar">⚡</div>
                  <div className="chat-bubble-col">
                    {streaming.status && <div className="status-caption">⚡ {streaming.status}</div>}
                    {streaming.dataList.map((d, i) => <DataCard key={i} data={d} />)}
                    {streaming.displayedText && <div className="chat-bubble">{streaming.displayedText}</div>}
                    {streaming.displayedText && streaming.dataList.map((d, i) => <DownloadReportButton key={i} data={d} />)}
                  </div>
                </div>
              )}
            </>
          )}
          <div ref={scrollAnchorRef} />
        </div>

        <ChatInput onSubmit={handleSend} disabled={!!streaming} />
      </main>
    </div>
  );
}
